<app-floating-shapes></app-floating-shapes>

<section class="home-hero">
    <div class="hero-glow hero-glow-a" aria-hidden="true"></div>
    <div class="hero-glow hero-glow-b" aria-hidden="true"></div>
    <div class="hero-grid-overlay" aria-hidden="true"></div>
    <div class="container hero-grid">
        <div class="hero-copy">
            <p class="hero-eyebrow" data-reveal style="--d: 0s"><span class="hero-eyebrow-dot" aria-hidden="true"></span>NordNative OÜ · BunnyCRM</p>
            <h1 class="hero-title" data-reveal style="--d: 0.08s">{{HOME_PAGE_HEADLINE01}}</h1>
            <p class="hero-subtitle" data-reveal style="--d: 0.16s">{{heroSubtitle}}</p>
            <div class="hero-actions" data-reveal style="--d: 0.24s">
                <a class="btn btn-light btn-lg" [routerLink]="['/contact']">Get a quote</a>
                <a class="btn btn-outline-light btn-lg" [routerLink]="['/products']">Explore products</a>
            </div>
            <ul class="hero-pills" data-reveal style="--d: 0.32s">
                <li>Strategy, design, build</li>
                <li>Secure, scalable stacks</li>
                <li>Global delivery</li>
            </ul>
        </div>
        <div class="hero-visual" data-reveal style="--d: 0.2s">
            <div class="hero-frame">
                <img src="images/home/home-hero.svg" alt="BunnyCRM connects marketing, sales, support and documents around one customer record" width="520" height="480" fetchpriority="high">
                <div class="hero-card">
                    <p class="hero-card-kicker">Delivery confidence</p>
                    <p class="hero-card-title">Clear scope, fast iterations</p>
                    <p class="hero-card-text">Measurable outcomes for every engagement — from first workflow to a full rollout.</p>
                </div>
            </div>
        </div>
    </div>
    <a class="hero-scroll" href="#what-we-do" (click)="scrollToSection($event, 'what-we-do')">
        <span>See what we do</span>
        <span class="hero-scroll-icon" aria-hidden="true"></span>
    </a>
</section>

<app-counter [widget]="counterWidget"></app-counter>

<section class="home-features" id="what-we-do">
    <div class="container section-header">
        <p class="section-kicker">What we do</p>
        <h2>End-to-end product delivery</h2>
        <p>From discovery and UX to engineering and growth, we help teams ship software that feels premium and performs reliably.</p>
    </div>
    <app-features></app-features>
</section>

<section class="home-steps" id="how-it-works">
    <div class="container">
        <div class="section-header">
            <p class="section-kicker">How it works</p>
            <h2>Launch your automation in weeks</h2>
            <p>We move fast with clear milestones so your sales and marketing teams see impact quickly.</p>
        </div>
        <div class="steps-grid">
            <div class="step-card" data-reveal style="--d: 0s">
                <div class="step-card-top">
                    <span class="step-index">01</span>
                    <span class="step-time">Week 1</span>
                </div>
                <h3>Audit &amp; align</h3>
                <p>Map your funnel, data sources, and key automations to prioritize what matters.</p>
            </div>
            <div class="step-card" data-reveal style="--d: 0.08s">
                <div class="step-card-top">
                    <span class="step-index">02</span>
                    <span class="step-time">Weeks 2–4</span>
                </div>
                <h3>Build &amp; connect</h3>
                <p>Integrate CRM, email, and analytics with clean workflows and smart triggers.</p>
            </div>
            <div class="step-card" data-reveal style="--d: 0.16s">
                <div class="step-card-top">
                    <span class="step-index">03</span>
                    <span class="step-time">Ongoing</span>
                </div>
                <h3>Optimize &amp; scale</h3>
                <p>Dashboards, experiments, and ongoing iteration to keep improving outcomes.</p>
            </div>
        </div>
    </div>
</section>

<section class="home-signal" id="capabilities">
    <div class="container">
        <div class="signal-header">
            <div>
                <p class="section-kicker">Focus areas</p>
                <h2>Everything your funnel needs, automated</h2>
            </div>
            <p class="signal-copy">BunnyCRM brings sales and marketing automation together in one platform, so your team spends less time on manual work and more time closing deals.</p>
        </div>
        <app-tag-cloud [keywords]="tagsCloud"></app-tag-cloud>
    </div>
</section>

<section class="business-sectors-section" id="industries">
    <div class="container">
        <div class="section-header">
            <p class="section-kicker">Industries</p>
            <h2>Business sectors we support</h2>
            <p>The same automation patterns — lead intake, follow-up, and reporting — adapted to how each industry actually sells.</p>
        </div>
        <div class="sectors-grid">
            @for (sector of sectorsOfWOrk; track sector.name; let i = $index) {
                <div class="sector-card" data-reveal [style.--d]="(i * 0.08) + 's'">
                    <div class="sector-icon"><i class="bi {{sector.icon}}"></i></div>
                    <span class="sector-name">{{ sector.name }}</span>
                    <span class="sector-detail">{{ sector.detail }}</span>
                </div>
            }
        </div>
    </div>
</section>

<section class="home-products" id="products">
    <div class="container">
        <div class="section-header" data-reveal>
            <p class="section-kicker">Our Products</p>
            <h2>Software we've built and use ourselves</h2>
            <p>Beyond client engagements, we build and maintain our own SaaS products — battle-tested in production and ready for your team.</p>
        </div>
        <div class="product-spotlight" data-reveal>
            <div class="product-spotlight-media">
                <img src="https://bunnycrm.com/images/brand/bunnycrm-logo-reversed.svg" alt="BunnyCRM logo" width="220" height="64" loading="lazy">
            </div>
            <div class="product-spotlight-body">
                <p class="product-eyebrow">Featured product</p>
                <h3>BunnyCRM</h3>
                <p>A customer relationship platform that keeps marketing, sales, support, and documents on one contact record — so campaigns, deals, and tickets stay connected.</p>
                <ul class="product-tags">
                    <li>Contact management</li>
                    <li>Sales tracking</li>
                    <li>Analytics</li>
                </ul>
                <div class="product-spotlight-actions">
                    <a class="product-link" href="https://bunnycrm.com" target="_blank" rel="noopener noreferrer">
                        Visit bunnycrm.com <span class="product-link-arrow" aria-hidden="true">↗</span>
                    </a>
                    <a class="product-link product-link-muted" [routerLink]="['/products']">
                        View all products <span class="product-link-arrow" aria-hidden="true">→</span>
                    </a>
                </div>
            </div>
        </div>
        <div class="module-grid">
            @for (product of products; track product.slug; let i = $index) {
                <a class="module-card" data-reveal [style.--d]="(i * 0.06) + 's'" [routerLink]="['/products', product.slug]">
                    <span class="module-card-media-wrap">
                        <img class="module-card-media" [src]="product.photo01" [alt]="product.photo01Alt" width="400" height="200" loading="lazy">
                    </span>
                    <span class="module-card-name">{{ product.name }}</span>
                    <span class="module-card-text">{{ product.description }}</span>
                    <span class="module-card-link">More details <span aria-hidden="true">→</span></span>
                </a>
            }
        </div>
    </div>
</section>

<section class="home-testimonials" id="testimonials">
    <div class="container">
        <div class="section-header" data-reveal>
            <p class="section-kicker">Client feedback</p>
            <h2>What our clients say</h2>
            <p>Real feedback from teams we've partnered with.</p>
        </div>
        <div class="testimonials-grid">
            @for (testimonial of testimonials; track testimonial.author; let i = $index) {
                <figure class="testimonial-card" data-reveal [style.--d]="(i * 0.1) + 's'">
                    <i class="bi bi-quote testimonial-quote-icon" aria-hidden="true"></i>
                    <blockquote class="testimonial-text">{{ testimonial.quote }}</blockquote>
                    <figcaption class="testimonial-author">
                        <span class="testimonial-avatar">{{ getInitials(testimonial.author) }}</span>
                        <div>
                            <span class="testimonial-name">{{ testimonial.author }}</span>
                            <span class="testimonial-role">{{ testimonial.role }}</span>
                        </div>
                    </figcaption>
                </figure>
            }
        </div>
    </div>
</section>

<section class="home-cta">
    <div class="container">
        <div class="home-cta-panel">
            <div>
                <p class="section-kicker cta-kicker">Next step</p>
                <h2>Run your business, not your software.</h2>
                <p>Tell us about your funnel and we'll map BunnyCRM to it — or explore the product suite first. No lengthy scoping call required to get started.</p>
            </div>
            <div class="home-cta-actions">
                <a class="btn btn-light btn-lg" [routerLink]="['/contact']">Get a quote</a>
                <a class="btn btn-outline-light btn-lg" [routerLink]="['/products']">Explore products</a>
            </div>
        </div>
    </div>
</section>
